import { observer } from "mobx-react-lite";
import { useAppStore, useGuestAppStore } from "@/store";
import { EntityNotFoundType, NotFoundPage } from "@/pages/not-found/not-found-page";
import { StandardModePadding } from "@/app/outlets/StandardModePadding";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { NoteEditor } from "@/pages/notes-view/note-editor";
import { AddToCollectionModal } from "@/components/modal/add-to-collection";
import { useTabLocationState } from "@/components/layout/components/tab-bar/use-tab-location-state";
import { FileUploadRejectedModal } from "@/components/modal/file-upload-rejected";
import { ImageUploadRejectedModal } from "@/components/modal/image-upload-rejected";
import { PdfPageLimitWarningModal } from "@/components/modal/pdf-page-limit-warning/pdf-page-limit-warning-modal";
import { RenameFileModal } from "@/components/modal/rename-file";
import { useLiveNoteSubscription } from "@/components/note/use-live-note-subscription";
import useDebouncedCallback from "@/domains/react/useDebouncedCallback";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { useTabContext } from "@/components/layout/tab-context";
import { useEffect } from "react";

export const StandardNotesViewPage = observer(function StandardNotesViewPage() {
  const store = useTabPageStore("notesViewPage");
  if (!store) return null;
  return <StandardNotesViewPageInternal page={store} />;
});

export const StandardNotesViewPageInternal = observer(function StandardNotesViewPageInternal({
  page,
}: {
  page: NotesViewPageStore;
}) {
  page.useEffects();
  useLiveNoteSubscription(page.noteId);

  const routerLocationState = useTabLocationState();
  // react to history version in router location state
  useEffect(() => {
    const routerHistoryVersion = routerLocationState?.historyVersion;
    const noteId = page.itemObservable?.id;
    if (!routerHistoryVersion || !noteId) return;

    const isAlreadyOpen =
      page.noteVersionHistory.currentNoteId === noteId &&
      page.noteVersionHistory.selectedSnapshot?.note_content_document_version ===
        routerHistoryVersion;
    if (isAlreadyOpen) return;

    page.noteVersionHistory.openNoteVersionHistory(noteId, routerHistoryVersion);
  }, [page.itemObservable?.id, page.noteVersionHistory, routerLocationState?.historyVersion]);

  return <NotesViewPage store={page} />;
});

export const GuestNotesViewPage = observer(function GuestNotesViewPage() {
  const { guestStore } = useGuestAppStore();
  guestStore.notesViewPage.useNoteIdFromRoutePath();
  return <NotesViewPage store={guestStore.notesViewPage} />;
});

interface NotesViewPageProps {
  store: INotesViewPageStore;
}

const NotesViewPage = observer<NotesViewPageProps>(({ store }) => {
  const { store: appStore } = useAppStore();
  const { activeTab } = useTabContext();
  const routerLocationState = useTabLocationState();

  const onNoteRendered = useDebouncedCallback(
    (noteId: string) => {
      if (!routerLocationState?.autoStartVoice || appStore.routing.noteIdParam !== noteId) {
        return;
      }

      // Handle autoStartVoice - either boolean or window structure
      if (typeof routerLocationState.autoStartVoice === "boolean") {
        void appStore.audio.createAndStartAudioRecording({
          associatedNoteId: noteId,
        });
      } else {
        void appStore.audio.createAndStartAudioRecording({
          expandVoicePopover: false,
          associatedNoteId: noteId,
          meetingEventWindowId: routerLocationState.autoStartVoice.windowId,
          config: {
            showOnboardingTooltip:
              routerLocationState.autoStartVoice.showOnboardingTooltip ?? false,
          },
        });
      }
      appStore.navigation.tabs.clearTabState(activeTab.id);
    },
    150,
    { leading: false, trailing: true }
  );

  if (store.showNotFound) {
    // Apply consistent padding for the NotFoundPage.
    return (
      <StandardModePadding>
        <NotFoundPage entityType={EntityNotFoundType.Note} />
      </StandardModePadding>
    );
  }

  return (
    <>
      {store.addToCollectionModal && <AddToCollectionModal store={store.addToCollectionModal} />}
      {store.fileUploadRejectedModal && (
        <FileUploadRejectedModal store={store.fileUploadRejectedModal} />
      )}
      {store.imageUploadRejectedModal && (
        <ImageUploadRejectedModal store={store.imageUploadRejectedModal} />
      )}
      {store.pdfPageLimitWarningModal && (
        <PdfPageLimitWarningModal store={store.pdfPageLimitWarningModal} />
      )}
      {store.renameFileModal && <RenameFileModal store={store.renameFileModal} />}
      <NoteEditor
        key={store.itemObservable?.id}
        noteObservable={store.itemObservable}
        openFileUploadRejectedModal={store.openFileUploadRejectedModal}
        openImageUploadRejectedModal={store.openImageUploadRejectedModal}
        openPdfPageLimitWarningModal={store.openPdfPageLimitWarningModal}
        openRenameFileModal={store.openRenameFileModal}
        findInNote={store.findInNote}
        myAccount={store.myAccount}
        goToMention={store.goToMention}
        highlightText={routerLocationState?.highlightText}
        autoFocus={routerLocationState?.autoFocus}
        autoFocusPosition={routerLocationState?.autoFocusPosition}
        skipInitialAnimation={routerLocationState?.skipEditorOpeningAnimation}
        initialPasteClipboardContent={routerLocationState?.initialPasteClipboardContent}
        onInitialPasteClipboardContentCaptured={() => {
          appStore.navigation.tabs.clearTabState(activeTab.id);
        }}
        onNoteRendered={onNoteRendered}
      />
    </>
  );
});
